import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, apiMultipart } from '../../lib/api.ts';
import {
  Area,
  Aviso,
  Boton,
  BotonArchivo,
  Campo,
  Casilla,
  Chip,
  EncabezadoPagina,
  FilaLista,
  Insignia,
  Selector,
  Tarjeta,
} from '../../componentes/ui.tsx';

type Seccion = 'inicio_h' | 'inicio_v' | 'plantillas_h' | 'productos_h' | 'galeria_h';

/** Dónde aparece cada banner y su proporción recomendada. */
const SECCIONES: { id: Seccion; nombre: string; medida: string; aspecto: string }[] = [
  { id: 'inicio_h', nombre: 'Inicio · horizontal', medida: '1152 × 192 px (6:1)', aspecto: 'aspect-[6/1]' },
  { id: 'inicio_v', nombre: 'Inicio · vertical', medida: '864 × 1080 px (4:5)', aspecto: 'aspect-[4/5] max-w-[180px]' },
  { id: 'plantillas_h', nombre: 'Elegir diseño · horizontal', medida: '1152 × 192 px (6:1)', aspecto: 'aspect-[6/1]' },
  { id: 'productos_h', nombre: 'Productos · horizontal', medida: '1152 × 192 px (6:1)', aspecto: 'aspect-[6/1]' },
  { id: 'galeria_h', nombre: 'Galería · horizontal', medida: '1152 × 192 px (6:1)', aspecto: 'aspect-[6/1]' },
];

const REGIONES = ['Costa', 'Sierra', 'Oriente', 'Galápagos'];

interface Campania {
  id: string;
  marcaId: string | null;
  tipo: 'POPUP' | 'BANNER';
  prioridad: number;
  regiones: string[];
  categoriaIds: string[];
  tipoIds: string[];
  clienteIds: string[];
  banners: { seccion: Seccion; assetId: string; imagenUrl: string | null }[];
  titulo: string;
  mensaje: string;
  imagenUrl: string | null;
  descuentoPct: number | null;
  desde: string;
  hasta: string;
  activo: boolean;
  soloUnaVez: boolean;
}

interface MarcaMin {
  id: string;
  nombre: string;
}

interface Clasificacion {
  id: string;
  nombre: string;
}

interface ClienteMin {
  id: string;
  razonSocial: string;
  nombreNegocio: string | null;
  region: string | null;
}

export function PaginaAdminCampanias() {
  const qc = useQueryClient();
  const [tipo, setTipo] = useState<'POPUP' | 'BANNER'>('POPUP');
  const [marcaId, setMarcaId] = useState('');
  const [prioridad, setPrioridad] = useState('0');
  const [titulo, setTitulo] = useState('');
  const [mensaje, setMensaje] = useState('');
  const [descuento, setDescuento] = useState('');
  const [desde, setDesde] = useState('');
  const [hasta, setHasta] = useState('');
  const [imagen, setImagen] = useState<File | null>(null);
  const [previa, setPrevia] = useState<string | null>(null);
  const [regiones, setRegiones] = useState<string[]>([]);
  const [categoriaIds, setCategoriaIds] = useState<string[]>([]);
  const [tipoIds, setTipoIds] = useState<string[]>([]);
  const [clienteIds, setClienteIds] = useState<string[]>([]);
  const [buscaCliente, setBuscaCliente] = useState('');
  /** Por sección: banner existente (assetId) o archivo nuevo por subir; siempre con previa. */
  const [banners, setBanners] = useState<Partial<Record<Seccion, { archivo?: File; assetId?: string; previa: string }>>>({});
  const [editandoId, setEditandoId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const campanias = useQuery({
    queryKey: ['admin-campanias'],
    queryFn: () => api<{ items: Campania[] }>('/admin/campanias'),
  });
  const marcas = useQuery({
    queryKey: ['admin-marcas'],
    queryFn: () => api<{ items: MarcaMin[] }>('/admin/marcas'),
  });
  const clientes = useQuery({
    queryKey: ['admin-clientes'],
    queryFn: () => api<{ items: ClienteMin[] }>('/admin/clientes'),
  });
  const categorias = useQuery({
    queryKey: ['cliente-categorias'],
    queryFn: () => api<{ items: Clasificacion[] }>('/admin/cliente-categorias'),
  });
  const tipos = useQuery({
    queryKey: ['cliente-tipos'],
    queryFn: () => api<{ items: Clasificacion[] }>('/admin/cliente-tipos'),
  });

  function limpiar() {
    setEditandoId(null);
    setTitulo('');
    setMensaje('');
    setDescuento('');
    setImagen(null);
    setPrevia(null);
    setPrioridad('0');
    setRegiones([]);
    setCategoriaIds([]);
    setTipoIds([]);
    setClienteIds([]);
    setBanners({});
    setError(null);
  }

  function cargarParaEditar(c: Campania) {
    setEditandoId(c.id);
    setTipo(c.tipo);
    setMarcaId(c.marcaId ?? '');
    setPrioridad(String(c.prioridad));
    setTitulo(c.titulo);
    setMensaje(c.mensaje);
    setDescuento(c.descuentoPct !== null ? String(c.descuentoPct) : '');
    setDesde(c.desde.slice(0, 10));
    setHasta(c.hasta.slice(0, 10));
    setImagen(null);
    setPrevia(c.imagenUrl);
    setRegiones(c.regiones);
    setCategoriaIds(c.categoriaIds);
    setTipoIds(c.tipoIds);
    setClienteIds(c.clienteIds);
    const existentes: typeof banners = {};
    for (const b of c.banners) {
      if (b.imagenUrl) existentes[b.seccion] = { assetId: b.assetId, previa: b.imagenUrl };
    }
    setBanners(existentes);
    setError(null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  async function subirAsset(archivo: File): Promise<string> {
    const forma = new FormData();
    forma.append('tipo', 'IMAGEN');
    forma.append('archivo', archivo);
    return (await apiMultipart<{ assetId: string }>('/admin/media', forma)).assetId;
  }

  const crear = useMutation({
    mutationFn: async () => {
      const entradas = Object.entries(banners) as [Seccion, { archivo?: File; assetId?: string; previa: string }][];
      if (tipo === 'BANNER' && entradas.length === 0) {
        throw new Error('Sube al menos un banner en alguna sección.');
      }
      let imagenAssetId: string | undefined;
      const bannersFinal: { seccion: Seccion; assetId: string }[] = [];
      if (tipo === 'POPUP' && imagen) imagenAssetId = await subirAsset(imagen);
      if (tipo === 'BANNER') {
        for (const [seccion, b] of entradas) {
          // existentes conservan su asset; sólo los archivos nuevos se suben
          const assetId = b.archivo ? await subirAsset(b.archivo) : b.assetId!;
          bannersFinal.push({ seccion, assetId });
        }
      }
      const cuerpo = {
        tipo,
        prioridad: Math.max(0, Math.min(100, Number(prioridad) || 0)),
        marcaId: marcaId || null,
        regiones,
        categoriaIds,
        tipoIds,
        clienteIds,
        banners: bannersFinal,
        titulo: titulo.trim(),
        mensaje: tipo === 'POPUP' ? mensaje.trim() : '',
        ...(imagenAssetId ? { imagenAssetId } : {}),
        ...(tipo === 'POPUP' && descuento ? { descuentoPct: Number(descuento) } : {}),
        desde: new Date(desde).toISOString(),
        hasta: new Date(`${hasta}T23:59:59`).toISOString(),
      };
      if (editandoId) {
        await api(`/admin/campanias/${editandoId}`, { metodo: 'PATCH', cuerpo });
      } else {
        await api('/admin/campanias', { metodo: 'POST', cuerpo });
      }
    },
    onSuccess: async () => {
      limpiar();
      await qc.invalidateQueries({ queryKey: ['admin-campanias'] });
    },
    onError: (e) => setError(e.message),
  });

  const alternar = useMutation({
    mutationFn: (c: Campania) =>
      api(`/admin/campanias/${c.id}`, { metodo: 'PATCH', cuerpo: { activo: !c.activo } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-campanias'] }),
  });

  const borrar = useMutation({
    mutationFn: (id: string) => api(`/admin/campanias/${id}`, { metodo: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-campanias'] }),
  });

  const [previsualizando, setPrevisualizando] = useState<Campania | null>(null);

  const listaClientes = (clientes.data?.items ?? []).filter((c) => {
    const q = buscaCliente.trim().toLowerCase();
    if (!q) return true;
    return (c.nombreNegocio ?? c.razonSocial).toLowerCase().includes(q) || c.razonSocial.toLowerCase().includes(q);
  });

  function nombreMarca(id: string | null): string | null {
    if (!id) return null;
    return marcas.data?.items.find((m) => m.id === id)?.nombre ?? null;
  }

  return (
    <main className="mx-auto w-full max-w-[1600px] space-y-4">
      <EncabezadoPagina
        titulo="Campañas"
        subtitulo="Pop-ups al entrar y banners por sección, segmentados por marca, región o cliente"
      />

      <form
        className="tarjeta space-y-4 p-5"
        style={editandoId ? { outline: '2px solid var(--color-acento-var)', outlineOffset: -2 } : undefined}
        onSubmit={(e) => {
          e.preventDefault();
          crear.mutate();
        }}
      >
        {editandoId ? (
          <p className="text-[13px] font-medium">
            Editando «{campanias.data?.items.find((c) => c.id === editandoId)?.titulo}»
          </p>
        ) : null}
        {/* Tipo */}
        <div className="flex flex-wrap items-center gap-2">
          <Chip activo={tipo === 'POPUP'} onClick={() => setTipo('POPUP')}>Ventana emergente</Chip>
          <Chip activo={tipo === 'BANNER'} onClick={() => setTipo('BANNER')}>Banners</Chip>
        </div>

        <div className="grid gap-3 sm:grid-cols-4">
          <div className="sm:col-span-2">
            <Campo
              etiqueta={tipo === 'POPUP' ? 'Título' : 'Nombre de la campaña'}
              value={titulo}
              onChange={(e) => setTitulo(e.target.value)}
              required
            />
          </div>
          <Campo etiqueta="Desde" type="date" value={desde} onChange={(e) => setDesde(e.target.value)} required />
          <Campo etiqueta="Hasta" type="date" value={hasta} onChange={(e) => setHasta(e.target.value)} required />
        </div>

        {tipo === 'POPUP' ? (
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <Area etiqueta="Mensaje" rows={2} value={mensaje} onChange={(e) => setMensaje(e.target.value)} required />
            </div>
            <Campo
              etiqueta="Descuento % (opcional)"
              type="number"
              value={descuento}
              onChange={(e) => setDescuento(e.target.value)}
            />
            <div>
              <p className="mb-1.5 text-[13px] text-suave">Imagen del pop-up (opcional)</p>
              <div className="flex items-center gap-3">
                {previa ? <img src={previa} alt="" className="interno h-16 w-16 object-cover" /> : null}
                <BotonArchivo
                  etiqueta="Elegir imagen"
                  accept="image/*"
                  nombre={imagen?.name ?? null}
                  onArchivo={(archivo) => {
                    setImagen(archivo);
                    setPrevia(archivo ? URL.createObjectURL(archivo) : null);
                  }}
                />
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-[13px] text-suave">
              Elige dónde va cada banner. Una sección sin banner no aparece ni ocupa espacio; si el formato
              es distinto, sube una versión adaptada a esa proporción.
            </p>
            <ul className="grid gap-3 lg:grid-cols-2">
              {SECCIONES.map((s) => {
                const b = banners[s.id];
                return (
                  <li key={s.id} className="interno space-y-2 p-3">
                    <div className="flex items-center justify-between gap-2">
                      <div>
                        <p className="text-sm font-medium">{s.nombre}</p>
                        <p className="text-xs text-suave">{s.medida}</p>
                      </div>
                      <div className="flex items-center gap-1.5">
                        {b ? (
                          <Boton
                            variante="texto"
                            tamano="sm"
                            onClick={() =>
                              setBanners((prev) => {
                                const copia = { ...prev };
                                delete copia[s.id];
                                return copia;
                              })
                            }
                          >
                            Quitar
                          </Boton>
                        ) : null}
                        <BotonArchivo
                          etiqueta={b ? 'Cambiar' : 'Subir banner'}
                          accept="image/*"
                          nombre={null}
                          onArchivo={(archivo) => {
                            setBanners((prev) => {
                              const copia = { ...prev };
                              if (archivo) copia[s.id] = { archivo, previa: URL.createObjectURL(archivo) };
                              else delete copia[s.id];
                              return copia;
                            });
                          }}
                        />
                      </div>
                    </div>
                    {b ? (
                      <div className={`interno w-full overflow-hidden ${s.aspecto}`}>
                        <img src={b.previa} alt="" className="h-full w-full object-cover" />
                      </div>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        {/* ── Segmentación: quién ve la campaña ── */}
        <div className="interno space-y-4 p-4">
          <div>
            <p className="text-[11px] font-medium uppercase tracking-wide text-suave">Segmentación</p>
            <p className="mt-0.5 text-xs text-suave">
              Quién ve la campaña. Las condiciones se combinan: un cliente la ve sólo si cumple TODAS.
              Lo que se deje vacío no filtra (= todos).
            </p>
          </div>
          <div className="grid gap-x-6 gap-y-4 lg:grid-cols-3">
          <div className="space-y-4">
            <div>
              <p className="mb-1.5 text-[13px] font-medium">Marca</p>
              <Selector aria-label="Marca" value={marcaId} onChange={(e) => setMarcaId(e.target.value)}>
                <option value="">Todas las marcas</option>
                {marcas.data?.items.map((m) => (
                  <option key={m.id} value={m.id}>{m.nombre}</option>
                ))}
              </Selector>
              <p className="mt-1 text-xs text-suave">
                Con una marca elegida, la campaña sólo la ven los clientes que han comprado esa marca.
              </p>
            </div>
            <div>
              <p className="mb-1.5 text-[13px] font-medium">Prioridad</p>
              {/* Stepper (− n +): más directo que un number a secas */}
              <div className="inline-flex items-center gap-1">
                <button
                  type="button"
                  aria-label="Bajar prioridad"
                  className="circulo h-11 w-11 !bg-superficie2 !shadow-none text-lg"
                  onClick={() => setPrioridad((v) => String(Math.max(0, (Number(v) || 0) - 1)))}
                >
                  −
                </button>
                <input
                  type="number"
                  min={0}
                  max={100}
                  value={prioridad}
                  onChange={(e) => setPrioridad(e.target.value)}
                  className="campo min-h-11 w-16 text-center [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                />
                <button
                  type="button"
                  aria-label="Subir prioridad"
                  className="circulo h-11 w-11 !bg-superficie2 !shadow-none text-lg"
                  onClick={() => setPrioridad((v) => String(Math.min(100, (Number(v) || 0) + 1)))}
                >
                  +
                </button>
              </div>
              <p className="mt-1 text-xs text-suave">
                Si a un cliente le calzan varias campañas, gana la de número mayor; en empate van rotando.
              </p>
            </div>
          </div>
          <div className="space-y-4">
            <div>
              <p className="mb-1.5 text-[13px] font-medium">Regiones</p>
              <div className="flex flex-wrap gap-2">
                {REGIONES.map((r) => (
                  <Chip
                    key={r}
                    activo={regiones.includes(r)}
                    onClick={() =>
                      setRegiones((prev) => (prev.includes(r) ? prev.filter((x) => x !== r) : [...prev, r]))
                    }
                  >
                    {r}
                  </Chip>
                ))}
              </div>
            </div>
            <p className="mb-1.5 text-[13px] font-medium">Categorías</p>
            <div className="flex flex-wrap gap-2">
              {categorias.data?.items.map((x) => (
                <Chip
                  key={x.id}
                  activo={categoriaIds.includes(x.id)}
                  onClick={() =>
                    setCategoriaIds((prev) => (prev.includes(x.id) ? prev.filter((i) => i !== x.id) : [...prev, x.id]))
                  }
                >
                  {x.nombre}
                </Chip>
              ))}
            </div>
            <p className="mb-1.5 mt-3 text-[13px] font-medium">Tipos de cliente</p>
            <div className="flex flex-wrap gap-2">
              {tipos.data?.items.map((x) => (
                <Chip
                  key={x.id}
                  activo={tipoIds.includes(x.id)}
                  onClick={() =>
                    setTipoIds((prev) => (prev.includes(x.id) ? prev.filter((i) => i !== x.id) : [...prev, x.id]))
                  }
                >
                  {x.nombre}
                </Chip>
              ))}
            </div>
          </div>
          <div>
            <p className="mb-1.5 text-[13px] font-medium">
              Clientes puntuales{clienteIds.length > 0 ? ` · ${clienteIds.length} elegidos` : ''}
            </p>
            <input
              value={buscaCliente}
              onChange={(e) => setBuscaCliente(e.target.value)}
              placeholder="Buscar cliente…"
              className="campo mb-2 w-full"
            />
            <div className="interno max-h-40 space-y-1 overflow-y-auto p-3">
              {listaClientes.map((c) => (
                <Casilla
                  key={c.id}
                  className="w-full"
                  activa={clienteIds.includes(c.id)}
                  onCambiar={(v) =>
                    setClienteIds((prev) => (v ? [...prev, c.id] : prev.filter((x) => x !== c.id)))
                  }
                  etiqueta={
                    <span className="truncate">
                      {c.nombreNegocio ?? c.razonSocial}
                      {c.region ? <span className="text-suave"> · {c.region}</span> : null}
                    </span>
                  }
                />
              ))}
              {listaClientes.length === 0 ? <p className="text-xs text-suave">Sin resultados.</p> : null}
            </div>
          </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Boton type="submit" disabled={crear.isPending}>
            {crear.isPending ? 'Guardando…' : editandoId ? 'Guardar cambios' : 'Crear campaña'}
          </Boton>
          {editandoId ? (
            <Boton variante="texto" onClick={limpiar}>Cancelar edición</Boton>
          ) : null}
        </div>
      </form>
      {error ? <Aviso>{error}</Aviso> : null}

      <Tarjeta>
        <ul className="divide-y divide-superficie2">
          {campanias.data?.items.map((c) => {
            const marca = nombreMarca(c.marcaId);
            const segmentos = [
              c.tipo === 'BANNER' ? `${c.banners.length} banner(s)` : null,
              c.prioridad > 0 ? `prioridad ${c.prioridad}` : null,
              marca,
              c.regiones.length > 0 ? c.regiones.join(', ') : null,
              c.categoriaIds.length > 0
                ? c.categoriaIds.map((i) => categorias.data?.items.find((x) => x.id === i)?.nombre ?? '?').join(', ')
                : null,
              c.tipoIds.length > 0
                ? c.tipoIds.map((i) => tipos.data?.items.find((x) => x.id === i)?.nombre ?? '?').join(', ')
                : null,
              c.clienteIds.length > 0 ? `${c.clienteIds.length} cliente(s)` : null,
            ].filter(Boolean);
            const miniatura = c.imagenUrl ?? c.banners.find((b) => b.imagenUrl)?.imagenUrl ?? null;
            return (
              <li key={c.id}>
                <FilaLista
                  avatar={
                    miniatura ? (
                      <span className="avatar bg-superficie2"><img src={miniatura} alt="" /></span>
                    ) : (
                      <span className="avatar">{c.titulo.charAt(0)}</span>
                    )
                  }
                  titulo={
                    <>
                      {c.titulo}
                      <span className="ml-2"><Insignia tono={c.tipo === 'BANNER' ? 'acento' : undefined}>{c.tipo === 'BANNER' ? 'Banners' : 'Pop-up'}</Insignia></span>
                      {c.descuentoPct !== null ? <span className="ml-2"><Insignia tono="acento">-{c.descuentoPct}%</Insignia></span> : null}
                      {!c.activo ? <span className="ml-2"><Insignia>pausada</Insignia></span> : null}
                    </>
                  }
                  subtitulo={`${c.desde.slice(0, 10)} – ${c.hasta.slice(0, 10)}${segmentos.length > 0 ? ` · ${segmentos.join(' · ')}` : ''}`}
                  acciones={
                    <>
                      <Boton variante="contorno" tamano="sm" onClick={() => setPrevisualizando(c)}>
                        Vista previa
                      </Boton>
                      <Boton variante="contorno" tamano="sm" onClick={() => cargarParaEditar(c)}>
                        Editar
                      </Boton>
                      <Boton variante="contorno" tamano="sm" onClick={() => alternar.mutate(c)}>
                        {c.activo ? 'Pausar' : 'Activar'}
                      </Boton>
                      <Boton variante="peligro" tamano="sm" onClick={() => borrar.mutate(c.id)}>Borrar</Boton>
                    </>
                  }
                />
              </li>
            );
          })}
          {campanias.data?.items.length === 0 ? <li className="py-6 text-sm text-suave">Sin campañas.</li> : null}
        </ul>
      </Tarjeta>

      {previsualizando ? (
        <VistaPreviaBanners campania={previsualizando} onCerrar={() => setPrevisualizando(null)} />
      ) : null}
    </main>
  );
}

/**
 * Mockup fiel de las páginas del cliente con los banners reales colocados:
 * mismo layout (nav, saludo, bento del inicio, grillas) en miniatura.
 */
function VistaPreviaBanners({ campania, onCerrar }: { campania: Campania; onCerrar: () => void }) {
  const de = (s: Seccion) => campania.banners.find((b) => b.seccion === s)?.imagenUrl ?? null;

  const Linea = ({ w, oscuro = false }: { w: string; oscuro?: boolean }) => (
    <div className={`h-2 rounded-full ${oscuro ? 'bg-white/30' : 'bg-negro/10'}`} style={{ width: w }} />
  );

  const Nav = () => (
    <div className="mb-3 flex items-center justify-between rounded-full bg-white px-3 py-2 shadow-[var(--sombra)]">
      <div className="flex items-center gap-2">
        <span className="h-4 w-4 rounded-full" style={{ backgroundColor: 'var(--color-acento-var)' }} />
        <Linea w="64px" />
      </div>
      <div className="flex items-center gap-3">
        <Linea w="28px" />
        <Linea w="36px" />
        <Linea w="28px" />
        <span className="h-5 w-5 rounded-full bg-negro" />
      </div>
    </div>
  );

  /** Lugar disponible sin banner: se marca punteado; en la página real NO ocupa espacio. */
  const Hueco = ({ vertical = false }: { vertical?: boolean }) => (
    <div
      className={`flex items-center justify-center rounded-2xl border-2 border-dashed border-negro/20 text-center text-[10px] leading-tight text-suave ${
        vertical ? 'aspect-[4/5] px-2' : 'h-12 w-full'
      }`}
    >
      Lugar de banner (vacío: no aparece)
    </div>
  );

  const TarjetaProducto = () => (
    <div className="rounded-2xl bg-white p-2 shadow-[var(--sombra)]">
      <div className="aspect-square rounded-xl bg-negro/5" />
      <div className="mt-1.5 space-y-1">
        <Linea w="70%" />
        <Linea w="45%" />
      </div>
    </div>
  );

  /** Página interna (diseños / productos / galería): título, chips, banner y grilla. */
  const MockGrid = ({ banner }: { banner: string | null }) => (
    <div className="rounded-[20px] p-3" style={{ backgroundColor: 'var(--fondo, #eef0f2)' }}>
      <Nav />
      <div className="mb-2 flex items-center justify-between">
        <div className="h-4 w-32 rounded-full bg-negro/15" />
        <div className="flex gap-1.5">
          <span className="h-5 w-14 rounded-full bg-negro" />
          <span className="h-5 w-14 rounded-full bg-white shadow-[var(--sombra)]" />
        </div>
      </div>
      {banner ? (
        <img src={banner} alt="" className="mb-2 w-full rounded-2xl object-cover" />
      ) : (
        <div className="mb-2"><Hueco /></div>
      )}
      <div className="grid grid-cols-4 gap-2">
        {[0, 1, 2, 3].map((i) => (
          <TarjetaProducto key={i} />
        ))}
      </div>
    </div>
  );

  const MockInicio = () => (
    <div className="rounded-[20px] p-3" style={{ backgroundColor: 'var(--fondo, #eef0f2)' }}>
      <Nav />
      <div className="mb-2 flex items-center justify-between">
        <div className="h-5 w-36 rounded-full bg-negro/20" />
        <span className="h-6 w-20 rounded-full bg-white shadow-[var(--sombra)]" />
      </div>
      {de('inicio_h') ? (
        <img src={de('inicio_h')!} alt="" className="mb-2 w-full rounded-2xl object-cover" />
      ) : (
        <div className="mb-2"><Hueco /></div>
      )}
      <div className="grid grid-cols-4 gap-2">
        {de('inicio_v') ? (
          <img src={de('inicio_v')!} alt="" className="aspect-[4/5] w-full rounded-2xl object-cover" />
        ) : (
          <Hueco vertical />
        )}
        {/* Tarjeta negra de métricas */}
        <div className="flex aspect-[4/5] flex-col justify-between rounded-2xl bg-negro p-2.5">
          <div className="space-y-1.5">
            <Linea w="60%" oscuro />
            <div className="h-5 w-10 rounded bg-white/80" />
          </div>
          <div className="flex gap-1.5">
            {[0, 1, 2].map((i) => (
              <span key={i} className="h-4 w-4 rounded-full bg-white/20" />
            ))}
          </div>
        </div>
        {/* "Lo último que te llegó" */}
        <div className="col-span-2 rounded-2xl bg-white p-2.5 shadow-[var(--sombra)]">
          <Linea w="55%" />
          <div className="mt-2 grid grid-cols-3 gap-1.5">
            {[0, 1, 2].map((i) => (
              <div key={i} className="aspect-square rounded-lg bg-negro/5" />
            ))}
          </div>
        </div>
      </div>
      {/* Accesos Promoción / Nuevo ingreso */}
      <div className="mt-2 grid grid-cols-2 gap-2">
        <div className="flex h-14 flex-col justify-between rounded-2xl p-2.5" style={{ backgroundColor: 'var(--color-acento-var)' }}>
          <span className="h-4 w-4 rounded-full bg-negro" />
          <Linea w="50%" />
        </div>
        <div className="flex h-14 flex-col justify-between rounded-2xl bg-negro p-2.5">
          <span className="h-4 w-4 rounded-full" style={{ backgroundColor: 'var(--color-acento-var)' }} />
          <Linea w="50%" oscuro />
        </div>
      </div>
    </div>
  );

  const MockPopup = () => (
    <div className="relative overflow-hidden rounded-[20px]" style={{ backgroundColor: 'var(--fondo, #eef0f2)' }}>
      <div className="p-3 opacity-40 blur-[1px]">
        <Nav />
        <div className="mb-2 h-5 w-36 rounded-full bg-negro/20" />
        <div className="grid grid-cols-4 gap-2">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="aspect-[4/5] rounded-2xl bg-white shadow-[var(--sombra)]" />
          ))}
        </div>
      </div>
      {/* La ventana emergente real, encima del inicio oscurecido */}
      <div className="absolute inset-0 flex items-center justify-center bg-black/40 p-6">
        <div className="w-full max-w-[260px] overflow-hidden rounded-2xl bg-white shadow-[var(--sombra-alta)]">
          {campania.imagenUrl ? (
            <img src={campania.imagenUrl} alt="" className="max-h-28 w-full object-cover" />
          ) : null}
          <div className="space-y-1.5 p-3">
            <p className="text-sm font-medium leading-tight">
              {campania.titulo}
              {campania.descuentoPct !== null ? (
                <span
                  className="ml-1.5 rounded-full px-1.5 py-0.5 text-[10px] font-semibold"
                  style={{ backgroundColor: 'var(--color-acento-var)', color: 'var(--color-acento-texto)' }}
                >
                  -{campania.descuentoPct}%
                </span>
              ) : null}
            </p>
            <p className="text-[11px] leading-snug text-suave">{campania.mensaje}</p>
            <div className="flex justify-end pt-1">
              <span className="rounded-full bg-superficie2 px-3 py-1 text-[10px] font-medium">Cerrar</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const paginas: { titulo: string; nodo: React.ReactNode }[] = [];
  if (campania.tipo === 'POPUP') {
    paginas.push({ titulo: 'Al entrar (ventana emergente)', nodo: <MockPopup /> });
  } else {
    // Todas las páginas con lugares de banner, tengan o no uno subido.
    paginas.push({ titulo: 'Inicio del cliente', nodo: <MockInicio /> });
    for (const [secc, titulo] of [
      ['plantillas_h', 'Elegir diseño'],
      ['productos_h', 'Sus productos'],
      ['galeria_h', 'Galería'],
    ] as const) {
      paginas.push({ titulo, nodo: <MockGrid banner={de(secc)} /> });
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-label={`Vista previa de ${campania.titulo}`}
      onClick={onCerrar}
    >
      <div
        className="tarjeta max-h-[92vh] w-full max-w-6xl space-y-4 overflow-y-auto p-6 shadow-[var(--sombra-alta)]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between">
          <h2 className="text-[20px] font-light">Así lo ve el cliente · {campania.titulo}</h2>
          <Boton variante="secundario" tamano="sm" onClick={onCerrar}>Cerrar</Boton>
        </div>
        <div className={`grid gap-4 ${paginas.length > 1 ? 'lg:grid-cols-2' : 'mx-auto max-w-2xl'}`}>
          {paginas.map((p) => (
            <div key={p.titulo}>
              <p className="mb-1.5 text-[11px] font-medium uppercase tracking-wide text-suave">{p.titulo}</p>
              {p.nodo}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
